import React from 'react';
import { ApplicationView, observable, useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import type Shape from '../../objects/Shape';
import { type DrawerProps } from './Drawer';
import DrawerLink from './DrawerLink';
import EditObjectFields from './EditObjectFields';
import styles from '../../styles.module.css';
import FieldsController from '../../controllers/FieldsController';
import DrawerContents, { type DrawerContentsProps } from './DrawerContents';
import type ShapeRecord from '../../shapes/ShapeRecord';
import { type fabric } from 'fabric';
import { type IDataStoreRecordBehaviors } from '../../collaboration/DataStoreRecordBehaviors';
import type SelectField from '../../fields/SelectField';

function colorForObjects(
  objects: Array<fabric.Object & IDataStoreRecordBehaviors>,
  statusField: SelectField
): React.CSSProperties['color'] | undefined {
  const statusId = statusField.valueForObject(objects[0]);
  if (!statusId) return;

  const statusOption = statusField.options.findById(statusId);
  if (!statusOption) return;

  const color: React.CSSProperties['color'] = statusOption.color;

  // Only return the color if it's the same option for each object
  if (
    objects.length > 1 &&
    !objects.every(object => statusField.valueForObject(object) === statusId)
  ) {
    return;
  }

  return color;
}

const Selection: React.FC<DrawerProps> = () => {
  const whiteboardController = useController(WhiteboardController);
  const { selection } = whiteboardController;

  const fieldsController = useController(FieldsController);

  if (!selection) {
    return (
      <DrawerContents>
        <aha-alert type='warning'>You have no objects selected.</aha-alert>
      </DrawerContents>
    );
  }

  const drawerProps: Omit<DrawerContentsProps, 'children'> = {};

  type StoreObject = fabric.Object & IDataStoreRecordBehaviors;

  let objects: Array<StoreObject> = [];
  switch (selection.type) {
    case 'activeSelection':
      drawerProps.title = 'Edit multiple objects';
      objects = observable(selection.getObjects() as StoreObject[]);
      break;

    case 'shape': {
      // Observe the shape
      const shapeObject = observable(selection as Shape);
      const { shape } = shapeObject;
      drawerProps.recordId = shapeObject.id;

      objects = [shapeObject as StoreObject];

      switch (shapeObject.shapeName) {
        case 'record':
          drawerProps.title =
            (shape as ShapeRecord).recordInfo?.name || 'Record';
          break;

        case 'note':
          drawerProps.titleHTML = shapeObject.textContent;
          break;

        default:
          console.error(
            `Unhandled shape type: ${shapeObject.shapeName}`,
            shapeObject
          );
          break;
      }
      break;
    }

    default:
      console.error(`Unhandled selection type: ${selection.type}`, selection);
      break;
  }

  return (
    <DrawerContents
      {...drawerProps}
      color={colorForObjects(objects, fieldsController.statusField)}
    >
      <div className='my-10'>
        <EditObjectFields objects={objects} />
      </div>
      <div
        className={styles.flexRow}
        style={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div>
          <aha-button
            kind='link'
            onClick={() => fieldsController.actionAddField('SelectField')}
          >
            <i className='fa-regular fa-circle-plus' />
            Add field
          </aha-button>
        </div>

        <div>
          <DrawerLink kind='primary' outline path='/settings/fields'>
            Manage fields
          </DrawerLink>
        </div>
      </div>
    </DrawerContents>
  );
};

export default ApplicationView(Selection);
